import { CheckCircledIcon, LockClosedIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import '../App.css';
import { Button } from '../components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';
import { formatDateToYYYYMMDD } from '../lib/dateHelper';
import BunnyInfoItem from '../components/bunnyInfoItem';

function Profile() {

    const { auth } = useContext(AuthContext);
    const form = useForm();
    const navigate = useNavigate();
    let { userId } = useParams();

    userId = parseInt(userId);

    const isOwnProfile = userId === auth.user.id;

    const [user, setUser] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        parent_email: '',
    });

    const fetchUserInformation = useCallback(() => {
        setIsLoading(true); // Start loading data

        api.get(`https://skhrf-api.cmdigital.se/v1/users?targetUserId=${userId}`)
            .then(response => {
                const userData = response.data;
                setUser(userData);

                setFormData({
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    address: userData.address || '',
                    parent_email: userData.parent_email || '',
                });
                setIsLoading(false); // Data fetched, set loading to false
            })
            .catch(error => {
                console.error('Error fetching user information:', error);
                setIsLoading(false); // Error occurred, set loading to false
            });
    }, [userId]);

    const sendUserInformation = (e) => {

        if (isLoading) return;

        e.preventDefault();

        setIsLoading(true);

        api.post(`https://skhrf-api.cmdigital.se/v1/users`, {
            targetUserId: userId,
            ...formData,
        }).then(response => {

            fetchUserInformation();
            setIsLoading(false);
            toast({
                variant: 'success',
                title: 'Informationen uppdaterades!',
                description: "Informationen uppdaterades utan problem.",
            });
        }).catch(error => {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
            setIsLoading(false);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    useEffect(() => {
        fetchUserInformation();
    }, [fetchUserInformation]);

    const membershipInformation = (
        auth?.user?.minimember ? 'Mini' :
            `${formatDateToYYYYMMDD(auth?.user?.membershipStarted)} - ${formatDateToYYYYMMDD(auth?.user?.membershipExpiration)}`
    );

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl mb-2'>
                    {isLoading ? (
                        <Skeleton className='w-64 h-8' />
                    ) : (
                        isOwnProfile ? 'Redigera din profil' : `Redigera ${user?.first_name} ${user?.last_name}s profil`
                    )}
                </h1>
                <div className='mb-3'>
                    <BunnyInfoItem label='Ditt medlemsskap' value={membershipInformation} isLoading={isLoading} styling='sm:w-1/2' />
                    <p className='text-sm font-normal text-muted-foreground'>Medlemsskapet hanteras av din förening.</p>
                </div>
                <Form {...form} >
                    <form className="flex flex-col gap-6 w-full sm:w-1/2">
                        <div className='grid grid-cols-2 gap-3'>
                            <FormField
                                control={form.control}
                                name="first_name"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel>{isOwnProfile ? 'Ditt förnamn' : 'Förnamn'}</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Johan" name="first_name" value={formData.first_name} onChange={handleChange} disabled={isLoading} autoComplete='given-name' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="last_name"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel>{isOwnProfile ? 'Ditt efternamn' : 'Efternamn'}</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Svensson" name="last_name" value={formData.last_name} onChange={handleChange} disabled={isLoading} autoComplete='family-name' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <FormField
                            control={form.control}
                            name="address"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>{isOwnProfile ? 'Din adress' : 'Adress'}</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Storgatan 1, 12345, Stora staden" name="address" value={formData.address} onChange={handleChange} disabled={isLoading} autoComplete='street-address' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>{isOwnProfile ? 'Ditt telefonnummer' : 'Telefonnummer'}</FormLabel>
                                    <FormControl>
                                        <Input placeholder="070 123 45 67" name="phone" type='tel' value={formData.phone} onChange={handleChange} disabled={isLoading} autoComplete='tel' />
                                    </FormControl>
                                    <FormDescription>
                                        Ett telefonnummer med 8 till 30 siffror, valfritt med ett inledande plustecken (+).
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>{isOwnProfile ? 'Din e-postadress' : 'E-postadress'}</FormLabel>
                                    <FormControl>
                                        <Input placeholder="hej@cmdigital.se" name="email" type='email' value={formData.email} onChange={handleChange} disabled={isLoading} autoComplete='email' />
                                    </FormControl>
                                    <FormDescription>
                                        Ändra e-postadress med varsamhet. Om du ändrar {isOwnProfile ? 'din' : 'någons'} e-postadress kommer {isOwnProfile ? 'du' : 'hen'} att behöva logga in med den nya e-postadressen nästa gång {isOwnProfile ? 'du' : 'hen'} besöker systemet. E-postadressen används exempelvis för att återställa lösenord.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="parent_email"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>E-postadress till förälder</FormLabel>
                                    <FormControl>
                                        <Input placeholder="kontakt@cmdigital.se" name="parent_email" type='email' value={formData.parent_email} onChange={handleChange} disabled={isLoading} autoComplete='off' />
                                    </FormControl>
                                    <FormDescription>
                                        Om {isOwnProfile ? 'du' : user.first_name} har angett en e-postadress för en förälder, kommer alla e-postmeddelanden som {isOwnProfile ? 'du' : 'hen'} tar emot även att skickas som kopia till föräldern. För att ta bort detta, lämna fältet tomt.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <Button type="submit" disabled={isLoading} onClick={sendUserInformation}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />Uppdatera</>}
                        </Button>
                    </form>
                </Form>
                {isOwnProfile && (
                    <div className='grid gap-3 pt-3'>
                        <Separator />
                        <h2 className='font-medium text-lg'>Återställ lösenord</h2>
                        <div className='text-sm'>Tryck på knappen nedan för att återställa ditt lösenord. Du behöver ha tillgång till din inkorg för att byta lösenord.</div>
                        <Button className='w-min-max w-full sm:w-2/6' variant='secondary' onClick={() => navigate('/reset-request')}>
                            <LockClosedIcon className='mr-3' />
                            Återställ lösenord
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Profile;
